import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2 } from 'lucide-react';
import { ModelCell } from './CatalogItemRow';
import type { NoticePhotos } from './NoticeProductImages';
import { displayVariantLabel } from '../variantLabel';
import { productMarkOf, type Job3D } from '../threeDApi';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useStableText } from '../../../components/StableText';

// THE WHOLE PRODUCTS IN THE DATABASE: one row per manual and colour whose product model was
// switched on in Phase 5, above the parts it is made of.
//
// A product has no catalog item — it is what the items add up to — so it could not be a row
// of the parts table, and for as long as it had no row anywhere, a product model switched into
// the database was in it without the database showing it. It is filed the way it is kept: by
// the manual and the submodel it carries (see productMarkOf, set_in_catalog).
export function ProductModelsTable({
  models, failed, notices, noticeFilter, submodelFilter, linked, onOpen,
}: {
  // Every model in the database; the products are picked out of them here. NULL until the
  // server has answered — a spinner where the table goes, not no table, which reads as "no
  // product in the database". `failed`: that ask did not come back.
  models: Job3D[] | null;
  failed: boolean;
  // Where a manual's name and a colour's label come from.
  notices: NoticePhotos[];
  noticeFilter: string;
  submodelFilter: string;
  // ?product=&submodel=: the row a parts table's "found in database" sent us to.
  linked: { docId: string; submodel: string } | null;
  onOpen: (job: Job3D) => void;
}) {
  const { t } = useLanguage();
  const st = useStableText();

  const rows = useMemo(() => {
    const by = new Map<string, { docId: string; submodel: string; models: Job3D[] }>();
    for (const m of models ?? []) {
      const mark = productMarkOf(m);
      if (!mark) continue;
      if (noticeFilter && mark.docId !== noticeFilter) continue;
      if (submodelFilter && mark.submodel !== submodelFilter) continue;
      const key = `${mark.docId}\u001f${mark.submodel}`;
      const row = by.get(key) ?? { ...mark, models: [] };
      row.models.push(m);
      by.set(key, row);
    }
    return [...by.values()];
  }, [models, noticeFilter, submodelFilter]);

  const linkedRef = useRef<HTMLTableRowElement>(null);
  const scrolled = useRef(false);
  useEffect(() => {
    if (!linked || scrolled.current || !linkedRef.current) return;
    linkedRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    scrolled.current = true;
  });

  if (models === null) {
    return failed ? (
      <p className="mb-8 text-center text-sm text-rose-600">{t('notice.catalog.modelsFailed')}</p>
    ) : (
      <div className="mb-8 flex justify-center text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!rows.length) return null;

  const cellCls = 'border border-slate-300 px-4 py-2 text-center align-middle';
  const headCls = `${cellCls} font-semibold text-slate-900`;

  return (
    <div className="mb-8 overflow-x-auto rounded-xl border-2 border-slate-300 bg-white shadow-sm">
      <table className="w-full border-collapse text-sm">
        <tbody>
          <tr>
            <th className={headCls}>{st('notice.catalog.product')}</th>
            <th className={headCls}>{st('notice.catalog.submodel')}</th>
            <th className={headCls}>{st('notice.catalog.models3d')}</th>
          </tr>
          {rows.map((row) => {
            const notice = notices.find((n) => n.docId === row.docId);
            const name = notice?.slug || notice?.docName;
            const label = notice?.images.find((im) => im.variant === row.submodel)?.variantLabel;
            const here = linked?.docId === row.docId && linked.submodel === row.submodel;
            return (
              <tr
                key={`${row.docId}-${row.submodel}`}
                ref={here ? linkedRef : undefined}
                className={here ? 'bg-blue-50' : undefined}
              >
                <td className={cellCls}>
                  {name ? (
                    <Link
                      to={`/view/${notice?.slug || row.docId}`}
                      className="inline-flex items-center gap-1 font-mono text-sm font-semibold text-blue-600 underline decoration-blue-600/40 underline-offset-4"
                    >
                      {name}
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                    </Link>
                  ) : (
                    <span className="font-mono text-sm italic text-slate-400">
                      {t('notice.catalog.schematicUnknownSource')}
                    </span>
                  )}
                </td>
                <td className={`${cellCls} text-xs text-slate-600`}>
                  {row.submodel ? displayVariantLabel(label || row.submodel) : '—'}
                </td>
                <ModelCell models={row.models} cellCls={cellCls} onOpen={onOpen} />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
